import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { capitalize } from 'lodash'
import { useLocation, useParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { Box, LinearProgress } from '@mui/material'
import { H3 } from '../../../layouts/TextStyles'
import Calendar from '../../../assets/icons/CalendarRounded.svg'
import Breadcrumb from '../../../components/Breadcrumb'
import { sectionApiSlice } from '../../../features/section/sectionSlice'
import { topicApiSlice } from '../../../features/topic/topicSlice'
import { SectionState, UserTopicState } from '../../../features/types'
import Topic from './components/Topic'
import TopicContent from './components/TopicContent'

const styles = {
    container: {
        display: 'grid',
        gridTemplateColumns: '45% 55%',
        alignItems: 'start',
        justifyContent: 'space-between',
        gap: '24px',

        '@media screen and (max-width: 500px)': {
            display: 'block',
        },
    },
    aside: {
        maxHeight: 750,
        overflow: 'auto',
    },
    flex: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',

        '@media screen and (max-width: 500px)': {
            alignItems: 'start',
            flexDirection: 'column',
        },
    },
    commentBox: {
        background: '#F1F3F5',
        display: 'flex',
        alignItems: 'center',
        padding: '8px 12px',
        borderRadius: 2,
        ml: 2,

        '@media screen and (max-width: 500px)': {
            ml: 0,
            mt: 1,
            mb: 2,
        },
    },
}

const SubjectPage = () => {
    const { formatMessage, locale } = useIntl()
    const { state } = useLocation()
    const courseTitle = state?.course
        ? state?.course?.[`title${capitalize(locale)}`]
        : ''
    const subjectTitle = state?.subject
        ? state?.subject?.[`title${capitalize(locale)}`]
        : ''
    const { userSubjectEnrollId } = useParams()
    const [selectedSection, setSelectedSection] = useState(null)
    const [selectedTopic, setSelectedTopic] = useState(null)
    const { data: topics = [], isFetching: isFetchingTopics } =
        topicApiSlice.useGetStudentTopicsBySubjectQuery(+userSubjectEnrollId)
    const {
        data: sectionDetails = null,
        isFetching: isFetchingSectionDetails,
    } = sectionApiSlice.useGetSectionDetailsByTopicEnrollQuery(
        {
            ...selectedSection,
            userTopicEnrollId: selectedTopic?.userTopicEnrollId,
        },
        {
            refetchOnMountOrArgChange: true,
            skip: !selectedSection && !selectedTopic,
        },
    )
    const { data: selectedTopicSections = [], isFetching: isFetchingSections } =
        sectionApiSlice.useGetStudentSectionsByTopicEnrollQuery(
            selectedTopic?.userTopicEnrollId,
            { skip: !selectedTopic },
        )
    const currentSectionIndex = selectedTopicSections.findIndex(
        (el) => el.id === selectedSection.id,
    )

    const handleSelect = (topic: UserTopicState, section: SectionState) => {
        setSelectedSection(section)
        setSelectedTopic(topic)
    }

    const onNextSection = () => {
        const isLastSection =
            currentSectionIndex === selectedTopicSections.length - 1

        if (currentSectionIndex > -1 && !isLastSection) {
            const nextSection = selectedTopicSections[currentSectionIndex + 1]
            setSelectedSection(nextSection)
        }
    }

    const onPreviousSection = () => {
        const isFirstSection = currentSectionIndex === 0

        if (currentSectionIndex > 0 && !isFirstSection) {
            const previousSection =
                selectedTopicSections[currentSectionIndex - 1]
            setSelectedSection(previousSection)
        }
    }

    return (
        <Wrapper>
            <Breadcrumb
                links={[
                    formatMessage({ id: 'myCourses' }),
                    courseTitle,
                    subjectTitle,
                ]}
            />

            <Content>
                <Header>
                    <Title>
                        <H3>
                            {state?.subject &&
                                state.subject?.[`title${capitalize(locale)}`] +
                                    ' ' +
                                    formatMessage({
                                        id: 'topics',
                                    }).toLowerCase()}
                        </H3>
                    </Title>

                    {state?.subject && (
                        <Box sx={styles.flex}>
                            <a
                                target="_blank"
                                rel="noreferrer"
                                href={state?.subject?.lessonLink}
                                style={{ marginLeft: 8 }}
                            >
                                {state?.subject?.lessonLink}
                            </a>

                            <Box sx={styles.commentBox}>
                                <img src={Calendar} />

                                {state?.subject?.lessonComment}
                            </Box>
                        </Box>
                    )}
                </Header>

                {isFetchingTopics && <LinearProgress />}

                <Box sx={styles.container}>
                    <Box sx={styles.aside}>
                        {topics.length
                            ? topics.map((topic, index) => {
                                  return (
                                      <Topic
                                          course={state?.course}
                                          subject={state?.subject}
                                          key={index}
                                          topic={topic}
                                          handleSelect={handleSelect}
                                          isFetchingSectionDetails={
                                              isFetchingSectionDetails
                                          }
                                          isFetchingSections={
                                              isFetchingSections
                                          }
                                          sectionDetails={sectionDetails}
                                          selectedSection={selectedSection}
                                          selectedTopic={selectedTopic}
                                          selectedTopicSections={
                                              selectedTopicSections
                                          }
                                          currentSectionIndex={
                                              currentSectionIndex
                                          }
                                          userSubjectEnrollId={
                                              +userSubjectEnrollId
                                          }
                                          onPreviousSection={onPreviousSection}
                                          onNextSection={onNextSection}
                                      />
                                  )
                              })
                            : formatMessage({ id: 'noData' })}
                    </Box>

                    <Box
                        sx={{
                            '@media screen and (max-width: 500px)': {
                                display: 'none',
                            },
                        }}
                    >
                        <TopicContent
                            course={state?.course}
                            subject={state?.subject}
                            isFetchingSectionDetails={isFetchingSectionDetails}
                            isFetchingSections={isFetchingSections}
                            sectionDetails={sectionDetails}
                            selectedSection={selectedSection}
                            selectedTopic={selectedTopic}
                            selectedTopicSections={selectedTopicSections}
                            currentSectionIndex={currentSectionIndex}
                            userSubjectEnrollId={+userSubjectEnrollId}
                            onPreviousSection={onPreviousSection}
                            onNextSection={onNextSection}
                        />
                    </Box>
                </Box>
            </Content>
        </Wrapper>
    )
}

export default SubjectPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`

const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;

    @media screen and (max-width: 500px) {
        margin-bottom: 10px;
    }
`

const Content = styled.div`
    background-color: #fff;
    border-radius: 16px;
    padding: 20px 40px;
    min-height: 80vh;

    @media screen and (max-width: 500px) {
        padding: 16px 8px;
    }
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    flex-wrap: wrap;
`
